import { IS_NATIVE } from '~/utils/platform'
import { setAppActive } from '~/composables/useAppLifecycle'
import { backExitOrArm, backLayerNames, configureBack, handleBack, type BackOutcome } from '~/composables/useBackStack'

/**
 * 📱 M6 (mobile gameplan): the native shell's two signals - the app leaving
 * the foreground and coming back (useAppLifecycle), and Android's back button
 * (useBackStack). Back never walks the browser history.
 *
 * The browser suites drive both from a page: window.__edBack() is one back
 * press and answers with what happened; window.__edAppState(false) sends the
 * app to the background and (true) brings it back; window.__edBackLayers()
 * lists the covers back would close, oldest first.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  configureBack({
    // A screen that put in no handler of its own (the draft, the Collection,
    // the policy pages) goes to the title, where the run is waiting; the
    // title arms, and a second press leaves. The run screens always have a
    // handler once mounted - before that, back waits rather than leave a fight.
    fallback: (): BackOutcome => {
      const path = router.currentRoute.value.path
      if (path === '/') return backExitOrArm()
      if (path === '/encounter' || path === '/map') return 'held'
      void nuxtApp.runWithContext(() => navigateTo('/'))
      return 'screen'
    },
  })
  const hooks = window as Window & {
    __edBack?: () => BackOutcome
    __edAppState?: (active: boolean) => void
    __edBackLayers?: () => string[]
  }
  hooks.__edBack = handleBack
  hooks.__edAppState = setAppActive
  hooks.__edBackLayers = backLayerNames

  if (!IS_NATIVE) return
  void import('@capacitor/app').then(({ App }) => {
    // Leaving is a trip to the background first: the save and the sync flush.
    configureBack({ exit: () => { setAppActive(false); App.exitApp().catch(() => setAppActive(true)) } })
    void App.addListener('backButton', () => { handleBack() })
    void App.addListener('appStateChange', ({ isActive }) => setAppActive(isActive))
  }).catch((error: unknown) => console.warn('The app shell plugin did not load.', error))
})
